// classic-style cards — deck construction, set validation, turn-in bonus,
// draw + reshuffle helpers. Pure: no DB, no Fastify, no side effects on
// caller state.
//
// Design choices (classic-faithful in v1):
//  - Deck = canonical classic-deck shape, INDEPENDENT of map size: 14 Infantry +
//    14 Cavalry + 14 Artillery + 2 Wild = 44 cards. The non-wild cards may
//    optionally carry a decorative `territoryId` for a future territory-match
//    bonus variant, but the relationship has no gameplay effect today.
//  - Set validity: 3-of-a-kind, one-of-each, or any combination with wilds
//    substituting for any type.
//  - Turn-in bonus schedule is configurable: `cycle` (warfish/fixed-set vibes)
//    or `incremental` (classic escalating bonuses).
//  - Shuffle uses the engine's seeded RNG so games stay replayable. When the
//    deck is empty and a draw is requested, the discard pile is reshuffled
//    back into the deck before the draw.

import type { GameEvent, GameState, MapDef, PlayerId } from './types.js'
import type { Rng } from './dice.js'
import {
  isValidSet as sharedIsValidSet,
  scheduleBonus as sharedScheduleBonus,
} from '@warcarp/api-types'
import type { Card, CardType, CardBonusSchedule } from '@warcarp/api-types'

// Re-export so callers that import from './cards.js' keep working unchanged.
export type { Card, CardType, CardBonusSchedule }

/**
 * Fixed deck composition, independent of map size. 14 + 14 + 14 + 2 = 44.
 * Mirrors the canonical classic deck and is the wire vocabulary the mobile UI
 * consumes. Do not change without coordinating with the mobile card surface.
 */
const DECK_INFANTRY = 14
const DECK_CAVALRY = 14
const DECK_ARTILLERY = 14
const DECK_WILDS = 2
const DECK_NON_WILD = DECK_INFANTRY + DECK_CAVALRY + DECK_ARTILLERY // 42
export const DECK_SIZE = DECK_NON_WILD + DECK_WILDS // 44
/**
 * Upper bound on how many distinct territories can have a card-association in
 * the canonical 44-card deck. Maps with MORE territories than this (e.g. usa
 * has 50) will simply not have a card for every territory — only the first
 * `TERRITORY_ASSOCIATION_LIMIT` territories (in map.territories order) appear
 * on cards. Maps with FEWER territories than this assign a territoryId to
 * exactly `map.territories.length` non-wild cards; the remaining non-wild
 * slots get `territoryId: undefined`.
 */
export const TERRITORY_ASSOCIATION_LIMIT = DECK_NON_WILD // 42

/**
 * Build the initial deck for a map.
 *
 * Composition (always exactly `DECK_SIZE` = 44 cards, regardless of map):
 *  - 14 infantry, 14 cavalry, 14 artillery (in that pre-shuffle slot order),
 *    then 2 wilds.
 *  - The first `min(42, map.territories.length)` non-wild slots receive a
 *    decorative `territoryId` from `map.territories` in order (slot 0 ←
 *    territory 0, slot 1 ← territory 1, …). This keeps the association
 *    deterministic per (map, deck index) and reproducible from a seed —
 *    the seed only affects the post-construction shuffle, not the
 *    territoryId assignment.
 *  - Remaining non-wild slots (only present when the map has fewer than 42
 *    territories) and both wilds carry `territoryId: undefined`.
 *  - Maps with MORE than 42 territories (e.g. usa has 50) simply don't get a
 *    card for every territory — only the first 42 territories appear on
 *    cards.
 *
 * The deck is then shuffled via the supplied RNG (Fisher-Yates) so the
 * shuffle is reproducible from the game seed.
 */
export function buildInitialDeck(map: MapDef, rng: Rng): Card[] {
  // Build the canonical 44-slot deck in a deterministic pre-shuffle order:
  // 14 infantry, then 14 cavalry, then 14 artillery, then 2 wilds.
  const slotTypes: CardType[] = [
    ...Array.from({ length: DECK_INFANTRY }, () => 'infantry' as const),
    ...Array.from({ length: DECK_CAVALRY }, () => 'cavalry' as const),
    ...Array.from({ length: DECK_ARTILLERY }, () => 'artillery' as const),
    ...Array.from({ length: DECK_WILDS }, () => 'wild' as const),
  ]

  // Walk the non-wild slots in order; pair slot i (0..41) with
  // map.territories[i] when present. This keeps the {slot → territoryId}
  // pairing deterministic for a given map and independent of the RNG.
  const territoryCount = map.territories.length
  const associated = Math.min(TERRITORY_ASSOCIATION_LIMIT, territoryCount)

  const cards: Card[] = slotTypes.map((type, i) => {
    if (type === 'wild') return { id: `card-${i}`, type }
    // Non-wild slots are indices 0..41; `i < associated` ⇔ slot has a paired
    // territory.
    if (i < associated) {
      const t = map.territories[i] as { id: string }
      return { id: `card-${i}`, type, territoryId: t.id }
    }
    return { id: `card-${i}`, type }
  })

  return shuffle(cards, rng)
}

/** Fisher-Yates shuffle using a seeded RNG. Pure: returns a new array. */
export function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const result = items.slice()
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1))
    ;[result[i], result[j]] = [result[j] as T, result[i] as T]
  }
  return result
}

/**
 * Validate that `cards` (exactly 3) forms a legal turn-in set.
 * Delegates to the shared implementation in @warcarp/api-types.
 */
export function isValidSet(cards: readonly Card[]): boolean {
  return sharedIsValidSet(cards)
}

/**
 * Compute the bonus armies awarded for the Nth set turn-in (zero-indexed in
 * `cardTurnIns`). The state's cardTurnIns counter is the pre-increment value.
 * Delegates to the shared implementation in @warcarp/api-types.
 */
export function scheduleBonus(cardTurnIns: number, schedule: CardBonusSchedule): number {
  return sharedScheduleBonus(cardTurnIns, schedule)
}

/**
 * Draw one card from the deck.
 *
 * If the deck is empty, the discard pile is reshuffled (via `rng`) and
 * becomes the new deck before drawing. Returns the drawn card plus the new
 * deck and discard arrays. Throws if both deck and discard are empty —
 * callers should ensure the deck is provisioned (with a fixed 44-card deck
 * and a 5-card hand cap, simultaneously emptying both deck and discard is
 * unreachable in practice; the throw is a defensive guard).
 */
export function drawCard(
  deck: readonly Card[],
  discard: readonly Card[],
  rng: Rng,
): { card: Card; deck: Card[]; discard: Card[] } {
  if (deck.length > 0) {
    const next = deck.slice()
    const card = next.pop() as Card
    return { card, deck: next, discard: discard.slice() }
  }
  if (discard.length === 0) {
    throw new Error('Cannot draw: deck and discard are both empty')
  }
  // Reshuffle discard → new deck, then draw the top card.
  const reshuffled = shuffle(discard, rng)
  const card = reshuffled.pop() as Card
  return { card, deck: reshuffled, discard: [] }
}

/**
 * Convenience: hand-size lookup that tolerates a missing entry. Used by the
 * placement-route guard to enforce the 5-card cap → must-turn-in flag.
 */
export function handSize(cards: Record<PlayerId, Card[]>, playerId: PlayerId): number {
  return (cards[playerId] ?? []).length
}

/**
 * Apply a card set turn-in for `playerId`.
 *
 * Effect:
 *  - The 3 cards (`set`) are removed from `playerId`'s hand.
 *  - They are pushed to `state.discard`.
 *  - `state.cardTurnIns` is incremented (pre-increment value drives the bonus).
 *  - `state.bonusArmies[playerId]` gains `scheduleBonus(prevTurnIns, schedule)`.
 *  - `state.mustTurnInCards` is cleared if the hand drops below 5.
 *  - A `card-turn-in` event is appended to `state.events`.
 *
 * Caller must verify that:
 *  - `state.phase === 'placement'`
 *  - `playerId` is the current player
 *  - `set` contains exactly 3 cards all present in the player's hand
 *  - `isValidSet(set)` is true
 *
 * Pure: never mutates `state`; returns a new object.
 */
export function applyCardTurnIn(
  state: GameState,
  playerId: PlayerId,
  set: readonly Card[],
): GameState {
  const used = new Set(set.map((c) => c.id))
  const hand = (state.cards?.[playerId] ?? []).filter((c) => !used.has(c.id))
  const newCards = { ...(state.cards ?? {}), [playerId]: hand }
  const newDiscard = [...(state.discard ?? []), ...set]
  const prevTurnIns = state.cardTurnIns ?? 0
  const bonus = scheduleBonus(prevTurnIns, state.config.cardBonusSchedule)
  const newBonusArmies = {
    ...(state.bonusArmies ?? {}),
    [playerId]: (state.bonusArmies?.[playerId] ?? 0) + bonus,
  }
  const newMustTurnIn = hand.length >= 5 ? (state.mustTurnInCards ?? false) : false

  const turnInEvent: GameEvent = {
    kind: 'card-turn-in',
    player: playerId,
    bonus,
    ts: Date.now(),
  }

  return {
    ...state,
    cards: newCards,
    discard: newDiscard,
    cardTurnIns: prevTurnIns + 1,
    bonusArmies: newBonusArmies,
    mustTurnInCards: newMustTurnIn,
    events: [...(state.events ?? []), turnInEvent],
  }
}
